import React from 'react'
import {useMultiStyleConfig, StackProps, TextProps, FlexProps, Flex, useBreakpointValue} from '@chakra-ui/react'
import {Icon, IconButton} from '_uiComponents'
import {useHeaderSizeWithoutBanner} from '_customHooks'
import themeBreakpoints from '../../../theme/foundations/breakpoints'

interface ScrollDownButtonProps {
    parentDataTestId: string
    arrowColor?: string
    animation?: boolean
    className?: string
    dataTestId?: string
    ariaLabel?: string
}

/**
 * ScrollDownButton component renders a button that, when clicked, scrolls the page down to the bottom of a specified parent element,
 * accounting for the header height at the current breakpoint.
 *
 * @param {string} parentDataTestId - The data-testid of the parent element.
 * @param {string} [arrowColor='color.icon.white'] - The color of the arrow icon.
 * @param {boolean} [animation=true] - Whether to animate the arrow icon.
 * @param {string} [className=''] - Additional class names to apply to the button.
 * @param {string} [dataTestId='scroll-down-button'] - The data-testid attribute for the button.
 * @param {string} [ariaLabel='Scroll down button'] - The aria-label for accessibility.
 *
 * @returns {JSX.Element} The rendered ScrollDownButton component.
 */
const ScrollDownButton = ({
    parentDataTestId,
    arrowColor = 'color.icon.white',
    animation = true,
    className = '',
    dataTestId = 'scroll-down-button',
    ariaLabel = 'Scroll down button',
}: ScrollDownButtonProps): JSX.Element => {
    const styles = useMultiStyleConfig('ScrollDownButton', {arrowColor, animation}) as Record<string, StackProps | FlexProps | TextProps>
    const headerSize = useHeaderSizeWithoutBanner(true)
    const breakpoints = Object.keys(themeBreakpoints.breakpoints)

    const currentBreakpoint = useBreakpointValue(
        breakpoints.reduce(
            (acc, key) => {
                acc[key] = key
                return acc
            },
            {} as Record<string, string>,
        ),
    )

    const handleClickScroll = (e: React.MouseEvent<HTMLButtonElement>) => {
        const animatedSlideContainer = (e.currentTarget as HTMLElement).closest('[data-testid="animated-slide-module-wrapper"]')
        if (animatedSlideContainer) {
            window.dispatchEvent(new CustomEvent('animated-scroll-down'))
            return
        }
        const parentElement = document.querySelector(`[data-testid=${parentDataTestId}]`) as HTMLElement
        const headerHeight = headerSize[currentBreakpoint ?? 'md']

        if (parentElement) {
            const scrollPos = parentElement.getBoundingClientRect().top + window.scrollY + parentElement.scrollHeight - headerHeight
            window.scrollTo({top: scrollPos, behavior: 'smooth'})
        }
    }

    return (
        <Flex sx={{...styles.wrapper}} className="cc-button-scroll-down">
            <IconButton
                sx={styles.svg}
                onClick={handleClickScroll}
                variant="iconButton"
                className={className}
                icon={<Icon name="IconChevronDown" size="md" />}
                size="iconButton"
                data-testid={dataTestId}
                aria-label={ariaLabel}
            />
        </Flex>
    )
}

export {ScrollDownButton}
